interface EnvVariables {
  DATA_SERVICE: string
  REALTIME_SERVICE: string
  CONSOLE_UI_SERVICE: string
  WORKSPACE_SERVICE: string
  STORAGE_SERVICE: string
  IAM_SERVICE: string
  AI_SERVICE: string
  ORCHESTRATION_SERVICE: string
  CLIENT: string
  SECRET: string
  WEBSOCKET_SERVICE: string
  NOTIFICATION_SERVICE: string
  SCHEDULER_SERVICE: string
  EMBED_SERVICE: string
  DOCS_SITE: string
  WORKSPACE_WEBSOCKET_SERVICE: string
  LOG_SOURCE: string
  ENVIRONMENT: string
}

function resolve(
  envKey: string,
  fallback: string,
  windowEnv: Record<string, string | undefined>,
): string {
  return windowEnv[envKey] || import.meta.env[envKey as keyof ImportMetaEnv] || fallback
}

function getEnvVariables(): EnvVariables {
  const env =
    (typeof window !== 'undefined' && window.__ENV__) || ({} as Record<string, string | undefined>)

  const aiBase = resolve('VITE_AI_SERVICE', 'http://localhost:7080', env)

  const dataService = resolve('VITE_DATA_SERVICE', 'http://localhost:6070', env)
  // Realtime SSE base (CEN-1302 Part B). Prefer an explicit override; otherwise
  // derive it from the data-service URL so prod works with no new env wiring:
  // both share the api host, where realtime is routed at `/realtime`
  // (Traefik strips the prefix). The direct-port dev value (…:6070) has no
  // `/data` suffix, so we fall back to the realtime container port.
  const realtimeService =
    resolve('VITE_REALTIME_SERVICE', '', env) ||
    (dataService.endsWith('/data')
      ? `${dataService.slice(0, -'/data'.length)}/realtime`
      : 'http://localhost:8090')

  return {
    DATA_SERVICE: dataService,
    REALTIME_SERVICE: realtimeService,
    CONSOLE_UI_SERVICE: resolve('VITE_CONSOLE_UI_SERVICE', 'http://localhost:5173', env),
    WORKSPACE_SERVICE: resolve('VITE_WORKSPACE_SERVICE', 'http://localhost:7062', env),
    STORAGE_SERVICE: resolve('VITE_STORAGE_SERVICE', 'http://localhost:5050', env),
    IAM_SERVICE: resolve('VITE_IAM_SERVICE', 'http://localhost:7060', env),
    AI_SERVICE: aiBase.endsWith('/ai') ? aiBase : `${aiBase}/ai`,
    ORCHESTRATION_SERVICE: resolve('VITE_ORCHESTRATION_SERVICE', 'http://localhost:8080', env),
    CLIENT: resolve('VITE_CLIENT', 'centrali-frontend', env),
    SECRET: resolve('VITE_SECRET', 'asdfasdfasdf', env),
    WEBSOCKET_SERVICE: resolve('VITE_WEBSOCKET_SERVICE', 'ws://localhost:8085', env),
    NOTIFICATION_SERVICE: resolve('VITE_NOTIFICATION_SERVICE', 'http://localhost:3000', env),
    SCHEDULER_SERVICE: resolve('VITE_SCHEDULER_SERVICE', 'http://localhost:5070', env),
    // Event Log embed origin (CEN-1301 / CEN-1349). The white-label iframe is
    // served from a dedicated subdomain (`embed.centrali.io/v1/event-log`), so
    // unlike REALTIME_SERVICE it can't be derived from the data-service URL.
    // Prod fallback hits the canonical host so no new env wiring is required
    // there; local dev sets VITE_EMBED_SERVICE=http://embed.centrali.localhost.
    EMBED_SERVICE: resolve('VITE_EMBED_SERVICE', 'https://embed.centrali.io', env),
    DOCS_SITE: resolve('VITE_DOCS_SITE', 'https://docs.centrali.io', env),
    WORKSPACE_WEBSOCKET_SERVICE: resolve(
      'VITE_WORKSPACE_WEBSOCKET_SERVICE',
      'ws://localhost:7063/ws',
      env,
    ),
    LOG_SOURCE: resolve('VITE_LOG_SOURCE', 'dev', env),
    ENVIRONMENT: resolve('VITE_ENVIRONMENT', 'development', env),
  }
}

export const Variables = getEnvVariables()
